import { useState, useEffect, useCallback } from "react";
import { generateFakeStationReadings } from "../fakeData";
import { apiFetch } from "../apiFetch";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

// A reading older than this is shown greyed with its age spelled out. The
// station's own measure interval is configurable (60s and 3600s have both been
// seen on the pilot unit), so this is deliberately generous — it is meant to
// catch "this channel stopped reporting", not "this arrived a bit late".
const STALE_AFTER_MS = 2 * 60 * 60 * 1000;

// Display order and units. Order is meteorological convention rather than the
// order the station happens to emit, which varies between frames.
//
// Units are the confirmed ones — see DataIngestionESM/lib/s2120.mjs. Pressure in
// particular arrives from the station in pascals and is converted to hPa
// server-side, so this renders what the API sends without rescaling anything.
const FIELDS = [
  { key: "air_temperature_c",  labelKey: "wsAirTemperature", unit: "°C",  decimals: 1 },
  { key: "humidity_pct",       labelKey: "wsHumidity",       unit: "%",   decimals: 0 },
  { key: "pressure_hpa",       labelKey: "wsPressure",       unit: "hPa", decimals: 1 },
  { key: "wind_speed_ms",      labelKey: "wsWindSpeed",      unit: "m/s", decimals: 1 },
  { key: "wind_gust_ms",       labelKey: "wsWindGust",       unit: "m/s", decimals: 1 },
  { key: "wind_direction_deg", labelKey: "wsWindDirection",  unit: "°",   decimals: 0, compass: true },
  { key: "illuminance_lux",    labelKey: "wsIlluminance",    unit: "lx",  decimals: 0 },
  { key: "uv_index",           labelKey: "wsUvIndex",        unit: "",    decimals: 0 },
];

// Rain is printed as figures above the table rather than as table rows, the way
// the drift chart prints Current and Peak drift: two numbers that are read
// together. The rate says how hard it is raining now, the 24 h total how much
// has fallen (#243). The rate is never charted, so this is the only place it
// appears.
function RainStat({ label, reading, unit, decimals, prefix = "", t }) {
  const stale = isStale(reading.measured_at);
  const num = Number(reading.value);
  const shown = Number.isFinite(num) ? num.toFixed(decimals) : String(reading.value);
  return (
    <div style={styles.stat}>
      <span style={styles.statLabel}>{label}</span>
      <span style={{ ...styles.statValue, ...(stale ? styles.staleValue : null) }}>
        {prefix}{shown}<span style={styles.unit}>{unit}</span>
      </span>
      <span style={styles.statAge}>
        {stale ? `${formatAge(reading.measured_at, t)} ${t("wsAgo")}` : formatAge(reading.measured_at, t)}
      </span>
    </div>
  );
}

const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

function compassPoint(deg) {
  const d = Number(deg);
  if (!Number.isFinite(d)) return null;
  return COMPASS[Math.round(((d % 360) + 360) % 360 / 45) % 8];
}

function formatAge(iso, t) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return t("wsJustNow");
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function isStale(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() > STALE_AFTER_MS;
}

// `simulated` renders generated data and makes no network request. This is not
// only cosmetic: every tier below superuser is simulated, and fetching here
// would put real site measurements in front of them.
export default function WeatherStationPanel({ deviceId, apiBaseUrl, label, t = (k) => k, simulated = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      let json;
      if (simulated) {
        json = generateFakeStationReadings(deviceId);
      } else {
        const url = `${apiBaseUrl}/api/station?device_id=${encodeURIComponent(deviceId)}`;
        const res = await apiFetch(url);
        if (!res.ok) throw new Error(`API error ${res.status}`);
        json = await res.json();
      }
      setData(json);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, apiBaseUrl, simulated]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const readings = data?.readings ?? {};
  const present = FIELDS.filter((f) => readings[f.key]?.value !== undefined);
  const battery = readings.battery;
  const withheld = data?.unverified_fields ?? [];
  const rainRate = readings.rain_rate_mmh;
  const rain24h = readings.rain_24h_mm;
  // Rain across a counter reset, or past the row cap, is not in the total, so
  // the figure is a lower bound and says so.
  const rainPartial = rain24h && (rain24h.unknown_intervals > 0 || rain24h.complete === false);

  return (
    <div style={{ ...styles.card, maxWidth: 1600 }}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{label ?? deviceId}</h2>
          <p style={styles.subtitle}>{deviceId}</p>
        </div>
        {battery?.value !== undefined && (
          <span style={battery.value < 20 ? styles.batteryLow : styles.batteryBadge}>
            {t("wsBattery")} {battery.value}%
            {isStale(battery.measured_at) && ` · ${formatAge(battery.measured_at, t)}`}
          </span>
        )}
      </div>

      <div style={styles.metaRow}>
        <span style={styles.lastUpdated}>
          {lastUpdated ? `${t("updated")} ${lastUpdated.toLocaleTimeString()}` : t("loading")}
        </span>
      </div>

      {loading ? (
        <div style={styles.placeholder}>{t("driftChartLoading")}</div>
      ) : error ? (
        <div style={styles.errorBox}>{t("driftChartError")}: {error}</div>
      ) : present.length === 0 ? (
        <div style={styles.placeholder}>{t("driftChartNoData")}</div>
      ) : (
        <>
        {(rainRate || rain24h) && (
          <div style={styles.statsRow}>
            {rainRate && (
              <RainStat label={t("wsRainRate")} reading={rainRate} unit="mm/h" decimals={1} t={t} />
            )}
            {rain24h && (
              <RainStat label={t("wsRain24h")} reading={rain24h} unit="mm" decimals={1}
                prefix={rainPartial ? "≥ " : ""} t={t} />
            )}
          </div>
        )}
        {rainRate && <p style={styles.rainNote}>{t("wsRainRateNote")}</p>}
        <table style={styles.table}>
          <tbody>
            {present.map((f) => {
              const r = readings[f.key];
              const stale = isStale(r.measured_at);
              const num = Number(r.value);
              const shown = Number.isFinite(num) ? num.toFixed(f.decimals) : String(r.value);
              const point = f.compass ? compassPoint(r.value) : null;
              return (
                <tr key={f.key} style={styles.row}>
                  <td style={styles.labelCell}>{t(f.labelKey)}</td>
                  <td style={{ ...styles.valueCell, ...(stale ? styles.staleValue : null) }}>
                    {shown}{f.unit && <span style={styles.unit}>{f.unit}</span>}
                    {point && <span style={styles.compass}>{point}</span>}
                  </td>
                  <td style={styles.ageCell}>
                    {/* Each field carries its own timestamp because the station
                        splits measurements and status across separate uplinks —
                        a panel that showed one "last updated" for the lot would
                        be wrong about most of them. */}
                    {stale ? `${formatAge(r.measured_at, t)} ${t("wsAgo")}` : formatAge(r.measured_at, t)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </>
      )}

      {/* The station also reports channels whose meaning or scaling is not
          confirmed. They are stored and returned by the API, but showing them
          here as readings would present guesses as measurements. */}
      {!loading && !error && withheld.length > 0 && (
        <p style={styles.withheld}>
          {t("wsUnverifiedNote")} <code style={styles.code}>{withheld.join(", ")}</code>
        </p>
      )}
    </div>
  );
}

const styles = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "20px 24px", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 },
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: "#111827" },
  subtitle: { margin: "2px 0 0", fontSize: 12, color: "#9ca3af" },
  batteryBadge: { background: "#ecfdf5", color: "#047857", padding: "3px 10px", borderRadius: 12, fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" },
  batteryLow: { background: "#fef2f2", color: "#b91c1c", padding: "3px 10px", borderRadius: 12, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" },
  metaRow: { display: "flex", alignItems: "center", marginBottom: 12 },
  lastUpdated: { marginLeft: "auto", fontSize: 12, color: "#9ca3af" },
  placeholder: { height: 120, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 },
  errorBox: { height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: 14, background: "#fef2f2", borderRadius: 6 },
  table: { width: "100%", borderCollapse: "collapse" },
  row: { borderBottom: "1px solid #f3f4f6" },
  labelCell: { padding: "10px 8px 10px 0", fontSize: 14, color: "#374151", fontWeight: 500 },
  valueCell: { padding: "10px 8px", fontSize: 18, fontWeight: 600, color: "#111827", textAlign: "right", whiteSpace: "nowrap" },
  staleValue: { color: "#9ca3af" },
  unit: { fontSize: 12, fontWeight: 500, color: "#6b7280", marginLeft: 4 },
  compass: { fontSize: 12, fontWeight: 600, color: "#1d4ed8", marginLeft: 8 },
  ageCell: { padding: "10px 0 10px 8px", fontSize: 12, color: "#9ca3af", textAlign: "right", width: 90, whiteSpace: "nowrap" },
  statsRow: { display: "flex", gap: 32, flexWrap: "wrap", marginBottom: 4 },
  stat: { display: "flex", flexDirection: "column", gap: 2 },
  // Same type scale as the drift chart's figures, so the two read as one family.
  statLabel: { fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" },
  statValue: { fontSize: 16, fontWeight: 600, color: "#111827", whiteSpace: "nowrap" },
  statAge: { fontSize: 11, color: "#9ca3af" },
  rainNote: { margin: "4px 0 12px", fontSize: 11, color: "#9ca3af", lineHeight: 1.5 },
  withheld: { marginTop: 14, marginBottom: 0, fontSize: 12, color: "#9ca3af", lineHeight: 1.5 },
  code: { background: "#f3f4f6", padding: "1px 5px", borderRadius: 4, fontSize: 11, color: "#6b7280" },
};
